import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { formatErrorMessage } from '../utils/errorFormatter';
import { Loader2, ChevronRight } from 'lucide-react';
import Logo from '../components/Logo';
import { usePageViewTracking, useTracking } from '../hooks/useTracking';

const LoginPage = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { trackEvent } = useTracking();
  
  // Track page view
  usePageViewTracking('login');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    trackEvent('login_attempt', { email });
    
    setLoading(true);
    try {
      await login(email, password);
      trackEvent('login_success', { email });
      toast.success('Connexion réussie');
    } catch (error) {
      console.error('Login error:', error);
      trackEvent('login_error', { email, error: error.response?.data?.detail });
      toast.error(formatErrorMessage(error, 'Échec de la connexion'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#1B4332] via-[#2D5A47] to-[#1B4332] p-12 flex-col justify-between relative overflow-hidden">
        {/* Texture grain subtile */}
        <div className="absolute inset-0 opacity-[0.06] bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIiB4PSIwIiB5PSIwIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxwYXRoIGQ9Ik0wIDBoMzAwdjMwMEgweiIgZmlsdGVyPSJ1cmwoI2EpIiBvcGFjaXR5PSIuMDUiLz48L3N2Zz4=')]"></div>
        
        <div className="relative z-10">
          <Logo size="large" variant="white" showText={true} />
          <p className="text-white/80 text-lg mt-4 max-w-md leading-relaxed">
            Simplifiez la gestion de vos commandes fournisseurs
          </p>
        </div>

        <div className="relative z-10 space-y-8">
          <div>
            <h2 className="text-3xl font-bold text-white mb-6">
              Pourquoi OrderLab ?
            </h2>
            <div className="space-y-4">
              {[
                { title: 'Gain de temps', desc: 'Créez vos commandes en quelques clics' },
                { title: 'Multi-fournisseurs', desc: 'Gérez tous vos fournisseurs au même endroit' },
                { title: 'PDF automatiques', desc: 'Générez des bons de commande professionnels' },
                { title: 'Import intelligent', desc: 'Importez vos factures via OCR (IA)' }
              ].map((feature, idx) => (
                <div key={idx} className="flex items-start gap-3 text-white/90">
                  <div className="bg-white/20 rounded-lg p-2 mt-1">
                    <ChevronRight className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{feature.title}</h3>
                    <p className="text-sm text-white/70">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
            <p className="text-white/90 text-sm italic">
              "OrderLab a transformé notre gestion des commandes. Plus simple, plus rapide, plus fiable."
            </p>
            <p className="text-white/70 text-xs mt-3">
              — Chef de cuisine, Restaurant Le Jardin
            </p>
          </div>
        </div>

        <p className="text-white/60 text-sm relative z-10">
          © 2025 OrderLab - Tous droits réservés
        </p>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center bg-[#F7F3E8] p-4">
        <div className="w-full max-w-md">
          {/* Logo mobile */}
          <div className="lg:hidden flex justify-center mb-8">
            <Logo size="large" showText={true} />
          </div>

          <Card className="border-0 shadow-2xl bg-white">
            <CardHeader className="text-center pb-6 pt-8">
              <CardTitle className="text-2xl font-bold text-[#1B4332]">
                Connexion
              </CardTitle>
              <CardDescription className="text-[#3E4A45]">
                Accédez à votre espace de gestion
              </CardDescription>
            </CardHeader>
            <CardContent className="px-8 pb-8">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#0F1F18] font-medium">
                    Adresse email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="votre@email.fr"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 border-[#DEC2AF] focus:border-[#1B4332] focus:ring-[#1B4332] bg-white"
                    data-testid="login-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#0F1F18] font-medium">
                    Mot de passe
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 border-[#DEC2AF] focus:border-[#1B4332] focus:ring-[#1B4332] bg-white"
                    data-testid="login-password-input"
                  />
                </div>
                
                {/* Mot de passe oublié */}
                <div className="text-right">
                  <button 
                    type="button"
                    className="text-sm text-[#1B4332] hover:text-[#2D5A47] font-medium hover:underline"
                  >
                    Mot de passe oublié ?
                  </button>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 bg-[#1B4332] hover:bg-[#2D5A47] text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Connexion en cours...
                    </>
                  ) : (
                    'Se connecter'
                  )}
                </Button>
              </form>

              {/* Divider */}
              <div className="relative my-8">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-[#DEC2AF]"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-[#A0A088]">
                    Nouveau sur OrderLab ?
                  </span>
                </div>
              </div>

              {/* Lien inscription */}
              <Link to="/register">
                <Button 
                  type="button"
                  variant="outline"
                  className="w-full h-12 border-2 border-[#1B4332] text-[#1B4332] hover:bg-[#1B4332] hover:text-white font-semibold rounded-xl transition-all duration-200"
                >
                  Créer un compte gratuit
                </Button>
              </Link>

              <p className="text-center text-xs text-[#A0A088] mt-6">
                Essai gratuit 14 jours • Aucune carte bancaire requise
              </p>
            </CardContent>
          </Card>

          {/* Features mobile */}
          <div className="lg:hidden mt-8 space-y-3">
            {[
              '✓ Gestion multi-fournisseurs',
              '✓ Import factures OCR (IA)',
              '✓ PDF automatiques'
            ].map((feature, idx) => (
              <p key={idx} className="text-sm text-[#3E4A45] text-center">
                {feature}
              </p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
